import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormBuilder,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {
  EventsFlowModel,
  EventsFlowItemModel,
  HhaCaregiverNotesSettingsModel,
  HhaPatientNotesSettingsModel,
  TranscriptionSettingsModel,
  UrlSettingsModel,
  EventFilterModel,
  ExtensionSettingsOverrideModel,
} from '../../api/models';
import { EventsFlowActionNodeTypes } from '../edit/models/events-flow-action-node-types';
import { EVENT_FLOW_FIELD_LABELS } from '../edit/node-parameters/constants/event-flow-field-labels.const';
import { TranscriptionProvider } from '../edit/forms/transcription-settings/transcription-settings.const';

@Injectable({ providedIn: 'root' })
export class EventsFlowDataFormService {
  private form: FormGroup;

  constructor(private fb: FormBuilder) {}

  get errorsMessage(): string {
    const errors: string[] = [];

    const eventFlowItems = this.eventFlowActionItemsForm?.controls || [];
    const eventFlowFilters = this.eventsFlowFiltersForm?.controls || [];

    eventFlowFilters.forEach(filterGroup => {
      const filterFormGroup = filterGroup as FormGroup;

      Object.entries(filterFormGroup.controls).forEach(([key, control]) => {
        if (control.invalid) {
          const label = this.getFieldLabel(filterFormGroup.get('key')?.value);

          errors.push(`Event Node: ${label} is required.`);
        }
      });
    });

    eventFlowItems.forEach((itemGroup, index) => {
      const actionSettings = itemGroup.get('actionSettings') as FormGroup;

      if (actionSettings) {
        Object.entries(actionSettings.controls).forEach(([key, control]) => {
          if (control.invalid) {
            const label = this.getFieldLabel(key);
            errors.push(`Action Node ${index + 1}: ${label} is required.`);
          }
        });
      }
    });

    return errors?.join('\n') || '';
  }

  get rawFormValue() {
    return this.form?.getRawValue() || {};
  }

  get eventsFlowForm(): FormGroup {
    return this.form;
  }

  get eventsFlowFiltersForm(): FormArray {
    return this.form?.get('eventConditions.filters') as FormArray;
  }

  get isEventsFlowFiltersFormValid(): boolean {
    return this.eventsFlowFiltersForm?.valid;
  }

  get isEventsFlowFormValid(): Boolean {
    return this.form?.valid;
  }

  get eventFlowActionItemsForm(): FormArray {
    return this.form?.get('eventFlowItems') as FormArray;
  }

  public initializeForm(editModel: EventsFlowModel): void {
    this.form = this.createEventDataForm(editModel);

    this.form.updateValueAndValidity();
    this.form.markAllAsTouched();
  }

  public getEventFlowActionItemSettings(
    actionId: string,
  ): UrlSettingsModel | HhaCaregiverNotesSettingsModel | HhaPatientNotesSettingsModel | TranscriptionSettingsModel {
    return this.getEventFlowActionItemSettingsForm(actionId)?.getRawValue() || {};
  }

  public getEventFlowActionItemSettingsForm(actionId: string): FormGroup {
    const eventFlowItems = this.form.get('eventFlowItems') as FormArray;
    const matchingItem = eventFlowItems.controls.find(control => control.get('id')?.value === actionId) as FormGroup;

    return matchingItem?.get('actionSettings') as FormGroup;
  }

  public addEventFlowActionItemForm(item: Partial<EventsFlowItemModel>): void {
    this.eventFlowActionItemsForm.push(this.createItemForm(item));
  }

  public removeEventFlowActionItemForm(actionId: string): void {
    const index = this.eventFlowActionItemsForm?.controls?.findIndex(control => control.get('id')?.value === actionId);

    if (index !== -1) {
      this.eventFlowActionItemsForm.removeAt(index);
    }
  }

  public addEventFlowEventFilter(filterKey: string): void {
    this.eventsFlowFiltersForm.push(this.createFilterForm({ key: filterKey, values: [] }));
  }

  public removeEventFlowEventFilter(filterIndex: number): void {
    this.eventsFlowFiltersForm.removeAt(filterIndex);
  }

  public resetEventFlowEventMainData(): void {
    this.form.get('eventFlowName').reset();
    this.eventsFlowFiltersForm.clear();
  }

  public isEventFlowActionItemSettingsFormValid(actionId: string): boolean {
    const form = this.getEventFlowActionItemSettingsForm(actionId);

    return form ? form.valid : true;
  }

  private createEventDataForm(editModel: EventsFlowModel): FormGroup {
    const { name, eventConditions, items } = editModel || {};
    const filters = eventConditions?.filters || [];

    return this.fb.group({
      eventFlowName: [name || ''],
      eventConditions: this.fb.group({
        filters: this.fb.array(filters.map(filter => this.createFilterForm(filter))),
      }),
      eventFlowItems: this.fb.array(items?.map(item => this.createItemForm(item)) || []),
    });
  }

  private createItemForm(item: Partial<EventsFlowItemModel>): FormGroup {
    return this.fb.group({
      id: [item.id],
      actionSettings: this.createActionSettingsForm(item),
    });
  }

  private createExtensionOverrideForm(o: ExtensionSettingsOverrideModel = { extensions: [] }): FormGroup {
    return this.fb.group({
      extensions: this.fb.control(o.extensions, [Validators.required, Validators.minLength(1)]),
      language: [o.language || '', Validators.required],
    });
  }

  private createFilterForm(filter: EventFilterModel): FormGroup {
    const { key, values, mode } = filter || {};

    return this.fb.group({
      key: [key, Validators.required],
      mode: [mode || 'include', Validators.required],
      values: this.fb.control(values, Validators.required),
    });
  }

  private createActionSettingsForm(item: Partial<EventsFlowItemModel>): FormGroup {
    const settings = item?.actionSettings || {};

    switch (item.actionType) {
      case EventsFlowActionNodeTypes.SendToUrl:
        const basicAuthMethod = settings.authMethod === 'basic';

        return this.fb.group({
          url: [settings.url || '', Validators.required],
          method: [settings.method || '', Validators.required],
          authMethod: [settings.authMethod || '', Validators.required],
          authUsername: [settings.authUsername || '', basicAuthMethod ? [Validators.required] : []],
          authPassword: [settings.authPassword || '', basicAuthMethod ? [Validators.required] : []],
        });

      case EventsFlowActionNodeTypes.TranscribeCallRecording:
        return this.fb.group({
          provider: [settings.provider || TranscriptionProvider.Deepgram, Validators.required],
          language: [settings.language || '', Validators.required],
          model: [settings.model || 'nova-3', Validators.required],
          summarySystemPrompt: [settings.summarySystemPrompt || ''],
          summaryUserPrompt: [settings.summaryUserPrompt || ''],
          extensionOverrides: this.fb.array(
            (settings.extensionOverrides || []).map(o => this.createExtensionOverrideForm(o)),
          ),
          skipStorage: [settings.skipStorage ?? false],
        });

      case EventsFlowActionNodeTypes.SendCaregiverNote:
        return this.fb.group({
          incomingAnsweredReasonId: [settings.incomingAnsweredReasonId || '', Validators.required],
          incomingMissedReasonId: [settings.incomingMissedReasonId || '', Validators.required],
          incomingMailboxReasonId: [settings.incomingMailboxReasonId || '', Validators.required],
          outgoingAnsweredReasonId: [settings.outgoingAnsweredReasonId || '', Validators.required],
          outgoingMissedReasonId: [settings.outgoingMissedReasonId || '', Validators.required],
          noteTemplate: [settings.noteTemplate || '', Validators.required],
        });

      case EventsFlowActionNodeTypes.SendPatientNote:
        return this.fb.group({
          incomingAnsweredNoteId: [settings.incomingAnsweredNoteId || '', Validators.required],
          incomingMissedNoteId: [settings.incomingMissedNoteId || '', Validators.required],
          incomingMailboxNoteId: [settings.incomingMailboxNoteId || '', Validators.required],
          outgoingAnsweredNoteId: [settings.outgoingAnsweredNoteId || '', Validators.required],
          outgoingMissedNoteId: [settings.outgoingMissedNoteId || '', Validators.required],
          noteTemplate: [settings.noteTemplate || '', Validators.required],
        });

      default:
        return this.fb.group({});
    }
  }

  private getFieldLabel(fieldName: string): string {
    return EVENT_FLOW_FIELD_LABELS[fieldName] || fieldName;
  }
}
